/**
 * Property card used in the V2 properties grid.
 *
 * Layout: image header (or placeholder) → content area with title, city,
 * type pill, rooms-count + occupied indicator, hidden indicator.
 *
 * The card itself navigates to the property detail (Rooms tab by default).
 * A small floating "Reminders" pill in the bottom-right of the image deep-
 * links to the same detail page on the Reminders tab. The pill stops
 * propagation so it doesn't trigger the parent <Link>.
 */
import { Link, useNavigate } from "react-router-dom";
import {
  Building,
  Building2,
  EyeOff,
  MailWarning,
  MapPin,
  MapPinOff,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { PropertyListItem } from "@/types/property";
import { hasPlottableCoordinates } from "@/lib/coordinates";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePropertyReminderStats } from "@/hooks/v2/usePropertyReminderStats";
import {
  usePropertyRentPaidStats,
  type PropertyRentPaidStats,
} from "@/hooks/v2/usePropertyRentPaidStats";

type Props = {
  property: PropertyListItem;
  /**
   * Set when this property came in through an owner's own submission and is
   * still waiting on review. Flags it in the grid so staff can see WHICH card
   * the Properties badge is counting, and who sent it.
   */
  submittedByName?: string | null;
  /**
   * Rent-paid stat MANAGED by the parent, when it batches every visible
   * property's stat in one fetch up front (the properties grid, which needs
   * this to sort by outstanding rent before pagination) instead of leaving
   * each card to fetch its own. Wrapped in an object so its mere PRESENCE —
   * even as `{ data: undefined }` while that batch is still loading — tells
   * the card not to ALSO fire its own per-property fetch; a bare `undefined`
   * prop would be indistinguishable from "parent not yet loaded" and the
   * card would fetch anyway during that window, defeating the batch's whole
   * purpose. Omit this prop entirely for a caller with no such batch (no
   * other caller currently exists, but the card should still work stand-alone).
   *
   * `isError` carries the batch's own failure through, because the card's own
   * query is disabled in this mode and so can never report one: without it a
   * failed batch would leave every card on "Loading…" forever instead of the
   * error badge the stand-alone path shows.
   */
  rentPaidStats?: {
    data: PropertyRentPaidStats | undefined;
    isError?: boolean;
  };
};

export function PropertyCard({
  property: p,
  submittedByName,
  rentPaidStats: managedRentPaidStats,
}: Props) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const occupancyPct =
    p.rooms_count > 0
      ? Math.round((p.occupied_count / p.rooms_count) * 100)
      : null;

  // % of this month's rent marked as paid, across active tenancies. Skip the
  // card's own fetch entirely whenever the caller manages this stat itself —
  // including while that caller's own batch is still loading.
  const hasManagedRentPaidStats = managedRentPaidStats !== undefined;
  const {
    data: rentPaidStatsFetched,
    isPending: rentPaidFetchPending,
    isError: rentPaidFetchError,
  } = usePropertyRentPaidStats(p.id, { enabled: !hasManagedRentPaidStats });
  const rentPaidStats = hasManagedRentPaidStats
    ? managedRentPaidStats.data
    : rentPaidStatsFetched;
  const rentPaidError = hasManagedRentPaidStats
    ? (managedRentPaidStats.isError ?? false)
    : rentPaidFetchError;
  const rentPaidPending = hasManagedRentPaidStats
    ? !rentPaidError && rentPaidStats == null
    : rentPaidFetchPending;
  const rentPaidPct = rentPaidStats?.pct ?? null;

  // Reminders badge: current-period overdue rows + open past-month rows
  // (COL-435), from the shared hook so this count matches the property's
  // Reminders tab and the other property cards.
  const { data: reminderStats, isError: reminderStatsError } =
    usePropertyReminderStats(p.id, {
      propertyRemindersEnabled: p.rent_reminders_enabled,
    });
  const overdueCount = reminderStats?.total ?? 0;
  const inkassoCount = reminderStats?.inkasso ?? 0;
  const hasReminders = overdueCount > 0;

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/admin/properties/${p.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ")
          navigate(`/admin/properties/${p.id}`);
      }}
      className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-2xl cursor-pointer"
    >
      <Card
        className={
          "overflow-hidden rounded-2xl transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-lg " +
          (inkassoCount > 0
            ? "ring-2 ring-rose-500/35 shadow-rose-500/10"
            : hasReminders
              ? "ring-2 ring-amber-500/30 shadow-amber-500/10"
              : "")
        }
      >
        <div className="relative aspect-[16/10] bg-muted">
          {p.cover_image ? (
            <img
              src={p.cover_image}
              alt={p.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <Building2 className="h-10 w-10 opacity-40" />
            </div>
          )}
          {/* Subtle bottom gradient for premium depth */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/25 to-transparent" />
          {/* Soft tint overlay when reminders exist (keeps text readable). */}
          {hasReminders ? (
            <div
              className={
                "pointer-events-none absolute inset-0 " +
                (inkassoCount > 0 ? "bg-rose-950/10" : "bg-amber-950/5")
              }
            />
          ) : null}
          {p.is_hidden && (
            <Badge
              variant="secondary"
              className="absolute top-2 left-2 gap-1 bg-background/90"
            >
              <EyeOff className="h-3 w-3" />
              {t("common.hidden", "Hidden")}
            </Badge>
          )}
          {p.type && (
            <Badge
              variant="secondary"
              className="absolute top-2 right-2 bg-background/90"
            >
              {p.type}
            </Badge>
          )}
          {/* An ungeocoded property renders no map on its public room pages —
              the guard hides the section rather than pinning visitors at 0/0.
              That silence is right for a visitor and wrong for an editor, who
              otherwise only finds out by opening a room (COL-48 follow-up). */}
          {!hasPlottableCoordinates(p.lat, p.lng) && (
            <Badge
              variant="secondary"
              className="absolute bottom-2 left-2 gap-1 border-amber-200 bg-amber-50 text-amber-800"
              title={t(
                "admin.property.badge.noLocationHint",
                "No coordinates — the public room pages show no map. Re-pick the address in Edit to add one."
              )}
            >
              <MapPinOff className="h-3 w-3" />
              {t("admin.property.badge.noLocation", "No location")}
            </Badge>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="space-y-1">
            <h3 className="font-semibold leading-tight line-clamp-1 tracking-tight">
              {p.title}
            </h3>
            {submittedByName ? (
              <Badge
                variant="outline"
                className="rounded-full border-amber-200 bg-amber-50 text-amber-700 font-medium"
              >
                {t("admin.properties.addedBy", "Added by {{name}}").replace(
                  "{{name}}",
                  submittedByName ?? ""
                )}
              </Badge>
            ) : null}
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{p.city}</span>
            </p>
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {p.apartments_count > 0 && (
                <Badge
                  variant="secondary"
                  className="gap-1 font-medium tabular-nums whitespace-nowrap"
                >
                  <Building className="h-3 w-3" />
                  {p.apartments_count}{" "}
                  {p.apartments_count === 1
                    ? t("admin.apartments.aptAbbrevOne", "Apt.")
                    : t("admin.apartments.aptAbbrevMany", "Apts.")}
                </Badge>
              )}
              <Badge
                variant="outline"
                className="font-medium tabular-nums whitespace-nowrap"
              >
                {p.rooms_count}{" "}
                {p.rooms_count === 1
                  ? t("admin.rooms.roomColumn", "Room")
                  : t("admin.rooms.roomsNoun", "Rooms")}
              </Badge>
              {p.rooms_count > 0 && (
                <Badge
                  variant={occupancyPct === 100 ? "default" : "secondary"}
                  className="font-normal whitespace-nowrap"
                >
                  {occupancyPct}% {t("admin.properties.occupied", "Occupied")}
                </Badge>
              )}
              {p.rooms_count === 0 && (
                <Badge
                  variant="outline"
                  className="font-normal text-muted-foreground whitespace-nowrap"
                >
                  {t("admin.properties.noRooms", "No rooms")}
                </Badge>
              )}
              {!p.cover_image && (
                <Badge
                  variant="outline"
                  className="font-normal text-muted-foreground whitespace-nowrap"
                >
                  {t("admin.properties.noImage", "No image")}
                </Badge>
              )}
            </div>

            <Link
              to={`/admin/properties/${p.id}?tab=reminders`}
              onClick={(e) => e.stopPropagation()}
              className={
                "shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold border transition-colors " +
                (hasReminders
                  ? inkassoCount > 0
                    ? "bg-rose-600 text-white border-rose-600/30 hover:bg-rose-700"
                    : "bg-rose-600 text-white border-rose-600/30 hover:bg-rose-700"
                  : "bg-muted/40 text-muted-foreground border-border/60 hover:bg-muted/60 hover:text-foreground")
              }
              title={
                reminderStatsError
                  ? t(
                      "admin.properties.remindersLoadError",
                      "Could not load reminders"
                    )
                  : hasReminders
                    ? `${overdueCount} ${t("admin.properties.overdue", "overdue")} ${
                        overdueCount === 1
                          ? t("admin.properties.tenantSingular", "tenant")
                          : t("admin.properties.tenantPlural", "tenants")
                      }${inkassoCount > 0 ? ` · ${inkassoCount} ${t("admin.properties.inkasso", "inkasso")}` : ""}`
                    : t("admin.properties.remindersNoun", "Reminders")
              }
              aria-label={t(
                "admin.properties.openRemindersAria",
                "Open reminders for this property"
              )}
            >
              <MailWarning className="h-3.5 w-3.5" />
              <span className="tabular-nums">
                {reminderStatsError
                  ? "—"
                  : hasReminders
                    ? overdueCount > 99
                      ? "99+"
                      : overdueCount
                    : "0"}
              </span>
            </Link>
          </div>

          {/* This month's rent-collection status */}
          <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-4">
            <span className="text-xs font-medium text-muted-foreground">
              {t("admin.properties.rentPaidLabel", "Rent collected")}
            </span>
            {rentPaidPending ? (
              <Badge
                variant="outline"
                className="shrink-0 whitespace-nowrap font-normal text-muted-foreground animate-pulse"
              >
                {t("admin.properties.rentPaidLoading", "Loading…")}
              </Badge>
            ) : rentPaidError ? (
              <Badge
                variant="outline"
                className="shrink-0 whitespace-nowrap font-normal text-muted-foreground"
              >
                {t(
                  "admin.properties.rentPaidLoadError",
                  "Could not load rent status"
                )}
              </Badge>
            ) : rentPaidPct === null ? (
              <Badge
                variant="outline"
                className="shrink-0 whitespace-nowrap font-normal text-muted-foreground"
              >
                {t("admin.properties.noRentDue", "No rent due")}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className={
                  "shrink-0 whitespace-nowrap font-semibold tabular-nums " +
                  (rentPaidPct >= 100
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25"
                    : rentPaidPct > 0
                      ? "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25"
                      : "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/25")
                }
              >
                {rentPaidPct}% {t("admin.properties.rentPaid", "paid")}
              </Badge>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
